import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import rtClient from '@/api/realtimeClient';
import { useAuth } from '@/api/AuthContext';
import { isReactaWebmasterStaff } from '@/utils/reactaStaffRole';
import { isOperationalPresenceEnabled } from '../config/operationalPresenceRuntime';

// OperationalPresenceProvider.jsx — ponto de montagem GLOBAL real da
// Presenca Operacional (Missao "Persistencia de Experiencia, Janela
// Desacoplavel, Multi-Monitor e Fundacao de Videowall", 2026-09-07).
//
// ACHADO DE AUDITORIA que motiva este arquivo: os Prompts 2/3/4 montavam
// OperationalPresenceRoot (dono da UNICA instancia de
// useOperationalMediaLifecycle, e portanto da Room LiveKit) dentro de
// src/Layout/layout.jsx, sob a alegacao (ADR-003 secao 9/15.7) de que o
// Layout "e montado uma unica vez e nao remonta por navegacao entre
// rotas". Essa alegacao NUNCA foi verdadeira em producao: cada pagina
// (src/pages/Logistics/Logistics.jsx, Safety.jsx, IncidentCommander.jsx,
// etc.) importa `Layout` diretamente e o renderiza dentro do PROPRIO
// componente de pagina (`return <Layout>...</Layout>`), e src/App.jsx
// roteia cada URL para um COMPONENTE DE PAGINA DIFERENTE via
// `<Routes><Route path="/Logistics" element={<Logistics/>}/>...`. Como
// paginas diferentes sao tipos de componente React diferentes na mesma
// posicao da arvore, o React desmonta a pagina/Layout anterior e monta a
// nova a CADA navegacao — inclusive tudo o que o Layout montava por
// dentro, como o OperationalPresenceRoot antigo. Combinado com o cleanup
// incondicional de useOperationalMediaLifecycle (`useEffect(() => () =>
// teardownRoom(), ...)`), isso significava que TROCAR DE CONSOLE COM A
// PRESENCA AUDIOVISUAL ATIVA DESCONECTAVA A ROOM LIVEKIT DE VERDADE —
// exatamente a regressao que esta missao existe para eliminar (ADR-003,
// Secao "Persistencia entre consoles": "navegar entre consoles nao deve
// desconectar a presenca audiovisual").
//
// Correcao: o singleton real (OperationalPresenceRoot) e montado aqui,
// UMA UNICA VEZ, em src/App.jsx, ACIMA de <Routes> — a UNICA posicao da
// arvore que de fato sobrevive a troca de rota (App.jsx nunca remonta por
// navegacao interna do Router; apenas os elementos DENTRO de <Routes>
// trocam). Layout.jsx continua existindo em toda pagina (chrome visual,
// sidebar, header) e continua remontando a cada navegacao como sempre
// fez — isso e inofensivo, porque a Presenca Operacional NAO mora mais
// dentro dele. Layout.jsx agora so CONSOME o estado de
// visibilidade/toggle via useOperationalPresenceUi(), nunca o dono dele.
//
// Nenhuma segunda Room, nenhum segundo hook, nenhuma segunda fonte de
// operationId foi criada: `operationId` e derivado exclusivamente de
// rtClient.subscribeOperationChange (a mesma Presence canonica ja usada
// em todo o resto do REACTA), e o proprio OperationalPresenceRoot
// (inalterado neste arquivo) continua sendo o UNICO chamador de
// useOperationalMediaLifecycle.
const OperationalPresenceRoot = React.lazy(() => import('./OperationalPresenceRoot'));

const OperationalPresenceUiContext = createContext(null);

const NOOP_UI = Object.freeze({
  visible: false,
  hasOperation: false,
  enabled: false,
  toggle: () => {},
  open: () => {},
  close: () => {},
});

// useOperationalPresenceUi — consumido por src/Layout/layout.jsx (botao
// "Presença Audiovisual" no header) para saber se deve mostrar o botao e
// para alternar a visibilidade do painel flutuante, sem possuir nenhum
// estado de midia/Presence por conta propria. Fora de um
// OperationalPresenceProvider (ex.: paginas publicas que nao usam Layout),
// devolve um estado inerte seguro em vez de lancar.
export function useOperationalPresenceUi() {
  const ctx = useContext(OperationalPresenceUiContext);
  return ctx || NOOP_UI;
}

export default function OperationalPresenceProvider({ children }) {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [operationId, setOperationId] = useState(() => rtClient.getActiveOperationId?.() || '');

  // subscribeOperationChange (src/api/realtimeClient.js) ja e o primitivo
  // canonico para acompanhar QUAL operacao esta ativa de forma reativa
  // (dispara imediatamente com o valor atual, e de novo a cada
  // OPERATION_CHANGED) — nenhum polling, nenhum segundo mecanismo de
  // deteccao de operacao ativa foi inventado aqui.
  useEffect(() => {
    const unsubscribe = rtClient.subscribeOperationChange((nextOperationId) => {
      setOperationId(nextOperationId || '');
    });
    return () => { if (unsubscribe) unsubscribe(); };
  }, []);

  const authoritativeRole = user?.role || user?.roleName || user?.role_name;
  const isReactaWebmaster = isReactaWebmasterStaff(authoritativeRole);
  const enabled = isOperationalPresenceEnabled();
  const hasOperation = Boolean(operationId);

  const ui = useMemo(() => ({
    visible,
    hasOperation,
    enabled,
    toggle: () => setVisible((current) => !current),
    open: () => setVisible(true),
    close: () => setVisible(false),
  }), [visible, hasOperation, enabled]);

  return (
    <OperationalPresenceUiContext.Provider value={ui}>
      {children}
      {enabled && hasOperation && (
        <React.Suspense fallback={null}>
          <OperationalPresenceRoot
            operationId={operationId}
            enabled={enabled}
            visible={visible}
            onClose={ui.close}
            isReactaWebmaster={isReactaWebmaster}
          />
        </React.Suspense>
      )}
    </OperationalPresenceUiContext.Provider>
  );
}
